/** Copyright (c) 2018 T. Rowe Price. All rights reserved. */

import './MainLayout.scss';

import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';

import { REQUEST_STATES } from 'state/requests/RequestStatusReducer';

import NavigationContainer from './Navigation/NavigationContainer';
import BalanceBar from './Bar/Balance/BalanceBar';
import {
  BrokerageFootnote,
  FactsetContainer
} from 'components/layouts/Footer/FooterUtil';
import AAMFooterContainer from './Footer/FooterUtil';
import { getCustomerWebView } from 'state/customer/CustomerSelectors';
import InterceptMessageModal from 'components/shared/InterceptMessage/InterceptMessageModal';
import ContactMethodMaintenanceModal from 'components/layouts/ContactMethodMaintenance/ContactMethodMaintenanceModal';
import MmrMessageBanner from 'components/layouts/MmrMessages/MmrMessageBanner';
import { ReactComponent as LogoBanner } from 'assets/logo-banner.svg';
import ExternalUrlNavigator from './Navigation/ExternalUrlNavigator';
import {
  getDefaultRequestStatus,
  getHasReceivedNasuApplications
} from 'state/requests/RequestStatusSelectors';
import { setReturningPAMUser } from 'state/view/ViewActions';
import { performedHomepageRedirect } from 'state/Actions';
import Store from 'state/Store';
import { isDashboardEntryPoint } from 'state/session/SessionSelectors';
import { redirectToNewDefaultHomepage } from 'state/preferences/PreferencesSelectors';
import { getInProgress } from 'components/shared/ErrorPage/NasuStateSelector';
import {
  buildQueryParamString,
  getQueryStringParams
} from 'support/BrowserUtils';
import {
  getThemeName,
  getView,
  isMobile,
  isSalesforceRepEnabled,
  onAccountsPage
} from 'state/view/ViewSelectors';
import {
  isDashboardOnly,
  isDashboardOrAccounts,
  isSummitPage
} from 'state/router/RouterSelectors';
import { SendTo } from 'App';
import {
  has529Only,
  getHasRetailTracAndWorkplaceOnly,
  mapProductTypeToiiFooterBoolean,
  getHasRetailTracAccountOnly,
  getHasRPSTracWorkplaceAccountsOnly
} from 'state/account/AccountSelectors';
import {
  non529OnlyLRNTaxesDashboardAccountsBenefits,
  owned529OnlyBrandingEnabled,
  retailTracEnabled
} from 'services/FeatureFlags';
import { getThemeNameFromAccounts } from 'state/account/AccountSelectors';
import { hasAppLoaded } from 'state/analytics/analyticsSelectors';
import LegalReferenceNumber from './LegalReferenceNumber/LegalReferenceNumber';
import {
  CLIENT_PORTAL,
  INTERNAL_CLIENT_PORTAL,
  INTERNAL_SECURE_SITE,
  privateAssetManagementUrl,
  SECURE_SITE,
} from "services/Externals";
import { ClientOptIn } from '@trp-ii-ii-component-library/ii-components';
import {getSessionStorageItem} from 'support/SessionStorageUtils';

export class MainLayout extends Component {
  constructor(props) {
    super(props);
  }

  componentDidUpdate(prevProps) {
    const { redirectToNewDefaultHomepage: prevRedirect } = prevProps;
    const { redirectToNewDefaultHomepage } = this.props;
    if (prevRedirect != redirectToNewDefaultHomepage) {
      Store.dispatch(performedHomepageRedirect());
    }
  }

  render() {
    const {
      isSalesforceRepEnabled,
      isEntry,
      isMobileApp,
      mmrMessages,
      requestStatus = {},
      view,
      redirectToNewDefaultHomepage,
      isLoadedNasuApplications,
      nasuApplications,
      showBalanceBar,
      showMMRMessage,
      onAccountsPage,
      has529Only,
      isSummitPage,
      isDashboardOrAccounts,
      isDashboardOnly,
      themeName,
      footerProductTypes,
      appLoaded,
      hasRetailTracPlusWorkPlaceOnly,
      hasRetailTracAccountOnly,
      hasRPSTracWorkplaceAccountsOnly,
      isCustomerWebView
    } = this.props;

    const isBalanceBarDataLoading = Object.values(requestStatus).some(
      (status) => status === REQUEST_STATES.requested
    );
    const hasFullPageError = Object.values(requestStatus).some(
      (status) => status === REQUEST_STATES.error
    );
    const isExternalRequest =
      window.location.pathname.includes('external-service');

    // move to selector to get from session
    const queryStringPairs = getQueryStringParams();
    const isReturningPAMSession = getSessionStorageItem('returningPVLUser');
    const isReturningPAMQuery = queryStringPairs.filter(
      (q) => q['returningPVLUser']
    ).length || isReturningPAMSession;

    if (!view.isReturningPAM && isReturningPAMQuery) {
      Store.dispatch(setReturningPAMUser());
    }

    const buildQueryParam = buildQueryParamString(queryStringPairs);

    const redirectToWorkplace = () => {
      if (retailTracEnabled()) {
        return (
          hasRPSTracWorkplaceAccountsOnly && !hasFullPageError && !isSummitPage
        );
      }
      return (
        view.isTracOnly ||
        view.isRPSOnly ||
        (view.hasWorkplaceRetirementAccountsOnly && !hasFullPageError)
      );
    };

    const redirectToSmallBusinessWorkplace = () => {
      return (
        hasRetailTracPlusWorkPlaceOnly && !hasFullPageError && !isSummitPage
      );
    };

    return (
      <Fragment>
        <div className={`main-layout-container ${themeName}`}>
          <ContactMethodMaintenanceModal />
          <ExternalUrlNavigator />

          <div className="print-logo-banner-container">
            <LogoBanner className="print-logo-banner-icon" />
          </div>
          {view.isPAM && !isReturningPAMQuery && !view.isReturningPAM && (
            <SendTo target={privateAssetManagementUrl} />
          )}
          {retailTracEnabled() &&
            hasRetailTracAccountOnly &&
            !hasFullPageError &&
            !isSummitPage &&
            !isCustomerWebView && (
              <Redirect to={'/smallbusiness-external-authorization'} />
            )}
          {redirectToWorkplace() && <Redirect to="/workplace" />}
          {redirectToSmallBusinessWorkplace() && (
            <Redirect to="/workplace-small-business" />
          )}
          {redirectToNewDefaultHomepage && <Redirect to="/accounts" />}
          {!isExternalRequest && isEntry && <InterceptMessageModal />}
          {view.hasNoAccounts &&
            isLoadedNasuApplications &&
            nasuApplications.length <= 0 &&
            !hasFullPageError &&
            !view.hasWorkplaceRetirementAccounts && (
              <Redirect to="/no-accounts" />
            )}
          {(view.hasNoAccounts || view.hasWorkplaceRetirementAccountsOnly) &&
            isLoadedNasuApplications &&
            nasuApplications.length > 0 && <Redirect to="/saved-nasu" />}
          {mmrMessages.hasMmrEvent && !view.hasNoAccounts && showMMRMessage && (
            <MmrMessageBanner message={mmrMessages.globalMessage} />
          )}
          { <Fragment>
              { isDashboardOnly && (
              <ClientOptIn
                isInternal={isCustomerWebView}
                defaultValue={false}
                options={[
                  {
                    name: 'Off',
                    value: 'aam-dashboard',
                    action: () => {
                      if(isCustomerWebView) {
                        document.location.href = `${INTERNAL_SECURE_SITE}${buildQueryParam}`;
                      }else {
                        document.location.href = `${SECURE_SITE}${buildQueryParam}`;
                      }
                    },
                    default: true
                  },
                  {
                    name: 'On',
                    value: 'client-portal',
                    action: () => {
                      if(isCustomerWebView) {
                        document.location.href = `${INTERNAL_CLIENT_PORTAL}${buildQueryParam}`;
                      } else {
                        document.location.href = `${CLIENT_PORTAL}${buildQueryParam}`;
                      }
                    }
                  }
                ]}
              />
              )}
            </Fragment>
          }
          {showBalanceBar && (
            <BalanceBar isBalanceBarDataLoading={isBalanceBarDataLoading} />
          )}
          <NavigationContainer
            hasError={hasFullPageError}
            hasDataError={view.hasExcessAccounts}
          />
        </div>

        <FactsetContainer
          onAccountsPage={onAccountsPage}
          has529Only={has529Only}
        />

        <BrokerageFootnote
          isDashboardOrAccounts={isDashboardOrAccounts}
          hasBrokerage={view.hasBrokerage}
        />
        {non529OnlyLRNTaxesDashboardAccountsBenefits() && (
          <LegalReferenceNumber />
        )}
        {!isSalesforceRepEnabled && (
          <AAMFooterContainer
            isMobileParam={isMobileApp}
            footerProductTypes={footerProductTypes}
            appLoaded={appLoaded}
          />
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  footerProductTypes: mapProductTypeToiiFooterBoolean(state),
  appLoaded: hasAppLoaded(state),
  isMobileApp: isMobile(state),
  isSalesforceRepEnabled: isSalesforceRepEnabled(state),
  mmrMessages: state.mmrMessages,
  requestStatus: getDefaultRequestStatus(state),
  view: getView(state),
  nasuApplications: getInProgress(state),
  isLoadedNasuApplications: getHasReceivedNasuApplications(state),
  redirectToNewDefaultHomepage: redirectToNewDefaultHomepage(state),
  showBalanceBar: isDashboardOrAccounts(state),
  showMMRMessage: isDashboardOrAccounts(state),
  isEntry: isDashboardEntryPoint(state),
  onAccountsPage: onAccountsPage(state),
  has529Only: has529Only(state),
  isSummitPage: isSummitPage(state),
  isDashboardOnly: isDashboardOnly(state),
  isDashboardOrAccounts: isDashboardOrAccounts(state),
  themeName: owned529OnlyBrandingEnabled()
    ? getThemeNameFromAccounts(state)
    : getThemeName(state),
  hasRetailTracPlusWorkPlaceOnly: getHasRetailTracAndWorkplaceOnly(state),
  hasRetailTracAccountOnly: getHasRetailTracAccountOnly(state),
  hasRPSTracWorkplaceAccountsOnly: getHasRPSTracWorkplaceAccountsOnly(state),
  isCustomerWebView: getCustomerWebView(state)
});

export default withRouter(connect(mapStateToProps)(MainLayout));
